import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Icon, IconButton } from '../../../components';
import { ApiError, apiErrorMessage, apiRequest, apiUpload } from '../../../lib/api';
import type { ListResponse } from '../../../lib/useCrud';

type AttachmentRow = {
  id: string;
  original_name: string | null;
  mime_type: string | null;
  file_size_bytes: number | null;
  uploaded_by_user_id: string | null;
  created_at: string;
};

function formatBytes(n: number | null): string {
  if (n == null) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function AttachmentsTab({ leadId }: { leadId: string }) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['lead-attachments', leadId],
    queryFn: () =>
      apiRequest<ListResponse<AttachmentRow>>(`/attachments?lead_id=${leadId}&limit=100`),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['lead-attachments', leadId] });

  const uploadMut = useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('lead_id', leadId);
      return apiUpload<{ data: AttachmentRow }>('/attachments', fd);
    },
    onSuccess: invalidate,
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiRequest<void>(`/attachments/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
    onError: (err) => alert(apiErrorMessage(err, 'Delete failed')),
  });

  async function pickAndUpload() {
    inputRef.current?.click();
  }

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setUploadError(null);
    try {
      await uploadMut.mutateAsync(files[0]);
    } catch (err) {
      setUploadError(err instanceof ApiError ? err.message : 'Upload failed');
    } finally {
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  async function downloadOne(id: string) {
    try {
      const res = await apiRequest<{ data: { url: string } }>(
        `/attachments/${id}/signed-url?expires_in=300`,
      );
      window.open(res.data.url, '_blank', 'noopener');
    } catch (err) {
      alert(err instanceof ApiError ? err.message : 'Failed to get download URL');
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <input
        ref={inputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={(e) => handleFiles(e.target.files)}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        {uploadMut.isPending && <span className="muted">Uploading…</span>}
        <Button variant="primary" icon="upload" onClick={pickAndUpload}>
          Upload file
        </Button>
      </div>
      {uploadError && <div className="alert">{uploadError}</div>}

      {list.isLoading && <div className="card card-body muted">Loading…</div>}
      {list.error && (
        <div className="alert">
          {list.error instanceof Error ? list.error.message : 'Failed to load'}
        </div>
      )}
      {list.data && list.data.data.length === 0 && (
        <div className="card card-body muted">No attachments yet.</div>
      )}
      {list.data && list.data.data.length > 0 && (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 28 }} />
                <th>Name</th>
                <th>Type</th>
                <th>Size</th>
                <th>Uploaded</th>
                <th style={{ width: 100 }} />
              </tr>
            </thead>
            <tbody>
              {list.data.data.map((a) => (
                <tr key={a.id}>
                  <td>
                    <Icon name="file" size={16} />
                  </td>
                  <td>{a.original_name ?? <span className="muted">—</span>}</td>
                  <td className="muted">{a.mime_type ?? '—'}</td>
                  <td className="tabular">{formatBytes(a.file_size_bytes)}</td>
                  <td className="muted">{new Date(a.created_at).toLocaleString()}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                      <IconButton
                        name="download"
                        label="Download"
                        onClick={() => downloadOne(a.id)}
                      />
                      <IconButton
                        name="trash"
                        label="Delete"
                        onClick={() => deleteMut.mutate(a.id)}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
